import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { fmtIST } from "@/shared/lib/time";
import api, { fmtErr } from "@/shared/lib/api";
import { Btn } from "@/shared/components/UI";
import { X, FileText, Lock, Plus, MessageSquare, Paperclip, ChevronRight, ChevronDown, Search, Layers } from "lucide-react";
import { toast } from "sonner";
import FileUpload from "@/shared/components/FileUpload";

/* Client Review section — rendered inside ProjectDetail for non-draft projects.
   Msg 902 — simplified, decluttered. One clean row per BOQ line; a focused
   drawer. The CSM relays client feedback as an APPEND-ONLY, locked instruction
   thread (with an optional clarity reference file) — never a general design
   upload (that's the designer's job in their portal). */

const STATUS = {
  na:        { label: "No review",   dot: "bg-zinc-300",    text: "text-zinc-400" },
  pending:   { label: "Awaiting client", dot: "bg-amber-400", text: "text-amber-700" },
  internal_review: { label: "With CSM · review", dot: "bg-sky-400", text: "text-sky-700" },
  modify:    { label: "Changes requested", dot: "bg-purple-400", text: "text-purple-700" },
  rejected:  { label: "Rejected",    dot: "bg-rose-400",    text: "text-rose-700" },
  completed: { label: "Approved",    dot: "bg-emerald-500", text: "text-emerald-700" },
  done:      { label: "Approved",    dot: "bg-emerald-500", text: "text-emerald-700" },
};

// NO design uploads from this page (Msg: designers upload ONLY via My Designs).
const RELAY_ROLES = new Set(["admin", "management", "csm"]);

const statusKey = (l) => l.client_design_approval || "na";
const bucketOf = (l) => {
  const s = statusKey(l);
  if (s === "completed" || s === "done") return "approved";
  if (s === "modify" || s === "rejected") return "attention";
  if (s === "pending" || s === "internal_review") return "awaiting";
  return "none";
};
const wallLabel = (l) => {
  const no = (l.wall_no || "").trim();
  const nm = (l.wall_name || "").trim();
  return no && nm ? `${no} · ${nm}` : (nm || no || l.material_name || "");
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "attention", label: "Changes", tone: "text-purple-700" },
  { key: "awaiting", label: "Awaiting", tone: "text-amber-700" },
  { key: "approved", label: "Approved", tone: "text-emerald-700" },
  { key: "none", label: "No review", tone: "text-zinc-500" },
];

export default function ClientReviewSection({ projectCode, boq, me, onReload }) {
  const [params] = useSearchParams();
  const [openFor, setOpenFor] = useState(() => {
    const want = params.get("openDesign");
    if (!want) return null;
    return (boq || []).find((l) => l.id === want) || null;
  });
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(() => new Set());
  const [didInit, setDidInit] = useState(false);

  // Build scope sections: each top-level line, with its wall children grouped
  // by wall_group. Standalone (childless) lines render as a single row.
  const sections = useMemo(() => {
    const lines = boq || [];
    const childrenByParent = new Map();
    for (const l of lines) {
      if (l.parent_id) {
        if (!childrenByParent.has(l.parent_id)) childrenByParent.set(l.parent_id, []);
        childrenByParent.get(l.parent_id).push(l);
      }
    }
    const tops = lines.filter((l) => !l.parent_id);
    return tops.map((top) => {
      const walls = childrenByParent.get(top.id) || [];
      // sub-group walls by wall_group
      const gmap = new Map();
      for (const w of walls) {
        const g = w.wall_group || "";
        if (!gmap.has(g)) gmap.set(g, []);
        gmap.get(g).push(w);
      }
      const groups = [...gmap.entries()].sort((a, b) =>
        a[0] === "" ? 1 : b[0] === "" ? -1 : a[0].localeCompare(b[0]))
        .map(([name, ws]) => ({ name, walls: ws }));
      const counts = { attention: 0, awaiting: 0, approved: 0, none: 0 };
      for (const w of walls) counts[bucketOf(w)]++;
      return { top, walls, groups, counts, hasWalls: walls.length > 0 };
    });
  }, [boq]);

  // Filter + search → only rows that match are shown; empty sections hide.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matchWall = (w) =>
      (filter === "all" || bucketOf(w) === filter) &&
      (!q || wallLabel(w).toLowerCase().includes(q) || (w.material_name || "").toLowerCase().includes(q));
    return sections.map((sec) => {
      if (!sec.hasWalls) {
        return { ...sec, _standalone: true, _show: matchWall(sec.top) };
      }
      const groups = sec.groups
        .map((g) => ({ ...g, walls: g.walls.filter(matchWall) }))
        .filter((g) => g.walls.length > 0);
      const total = groups.reduce((n, g) => n + g.walls.length, 0);
      return { ...sec, groups, _matchCount: total, _show: total > 0 };
    }).filter((s) => s._show);
  }, [sections, query, filter]);

  // Auto-expand sections that have items needing attention on first load.
  useEffect(() => {
    if (didInit || sections.length === 0) return;
    const open = new Set();
    for (const s of sections) {
      if (s.hasWalls && (s.counts.attention > 0 || s.counts.awaiting > 0)) open.add(s.top.id);
    }
    // if nothing needs attention, expand the first section so it's not all collapsed
    if (open.size === 0 && sections[0]?.hasWalls) open.add(sections[0].top.id);
    setExpanded(open);
    setDidInit(true);
  }, [sections, didInit]);

  if (!boq || boq.length === 0) return null;
  const canRelay = me && RELAY_ROLES.has(me.role);

  const pending = boq.filter((l) => l.client_design_approval === "pending").length;
  const approved = boq.filter((l) => ["completed", "done"].includes(l.client_design_approval)).length;
  const changes = boq.filter((l) => ["modify", "rejected"].includes(l.client_design_approval)).length;
  const totalWalls = boq.filter((l) => l.parent_id).length;

  const toggle = (id) => setExpanded((cur) => {
    const next = new Set(cur);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allOpen = visible.every((s) => s._standalone || expanded.has(s.top.id));
  const setAll = () => setExpanded(allOpen ? new Set() : new Set(visible.filter((s) => !s._standalone).map((s) => s.top.id)));

  return (
    <div className="bg-white border border-zinc-200 rounded-xl shadow-sm p-5 sm:p-6 mb-6" data-testid="client-review-section">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400">Client approval</div>
          <h3 className="text-lg font-semibold tracking-tight text-zinc-900 mt-0.5">Design review</h3>
        </div>
        <div className="text-xs text-zinc-500 tabular-nums">
          {changes > 0 && <span className="text-purple-700 font-medium">{changes} changes</span>}
          {changes > 0 && (pending > 0 || approved > 0) && " · "}
          {pending > 0 && <span className="text-amber-700">{pending} awaiting</span>}
          {pending > 0 && approved > 0 && " · "}
          {approved > 0 && <span className="text-emerald-700">{approved} approved</span>}
          {totalWalls > 0 && <span className="text-zinc-400"> · {totalWalls} walls</span>}
        </div>
      </div>

      {/* Toolbar — search + status filter + expand all */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search wall or scope…"
                 className="w-full h-9 pl-9 pr-3 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300"
                 data-testid="dr-search" />
        </div>
        <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-0.5" data-testid="dr-filters">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`dr-filter-${f.key}`}
                    className={`px-2.5 h-8 rounded-md text-xs font-medium transition-colors ${filter === f.key ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500 hover:text-zinc-800"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={setAll} data-testid="dr-expand-all"
                className="h-9 px-3 rounded-lg border border-zinc-200 text-xs font-medium text-zinc-600 hover:bg-zinc-50">
          {allOpen ? "Collapse all" : "Expand all"}
        </button>
      </div>

      {/* Sections */}
      {visible.length === 0 ? (
        <div className="py-10 text-center text-sm text-zinc-400" data-testid="dr-no-results">No line items match this filter.</div>
      ) : (
        <div className="space-y-2.5">
          {visible.map((sec) => sec._standalone ? (
            <div key={sec.top.id} className="border border-zinc-200 rounded-xl overflow-hidden">
              <ReviewRow line={sec.top} onOpen={() => setOpenFor(sec.top)} />
            </div>
          ) : (
            <SectionCard key={sec.top.id} sec={sec} open={expanded.has(sec.top.id)}
                         onToggle={() => toggle(sec.top.id)} onOpenWall={setOpenFor} />
          ))}
        </div>
      )}

      {openFor && (
        <DesignDrawer projectCode={projectCode} line={openFor} me={me}
                      onClose={() => setOpenFor(null)}
                      onSaved={() => { setOpenFor(null); onReload && onReload(); }}
                      canRelay={canRelay} />
      )}
    </div>
  );
}

function MiniCount({ n, tone, title }) {
  if (!n) return null;
  return <span title={title} className={`inline-flex items-center gap-1 text-[11px] font-medium ${tone}`}>
    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />{n}
  </span>;
}

function SectionCard({ sec, open, onToggle, onOpenWall }) {
  const { top, groups, counts, walls } = sec;
  const shown = groups.reduce((n, g) => n + g.walls.length, 0);
  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden" data-testid={`dr-section-${top.id}`}>
      <button onClick={onToggle} data-testid={`dr-section-toggle-${top.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${open ? "bg-zinc-50" : "bg-white hover:bg-zinc-50"}`}>
        <span className="w-8 h-8 rounded-lg bg-zinc-100 text-zinc-500 flex items-center justify-center flex-shrink-0">
          <Layers className="w-4 h-4" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-zinc-900 truncate">{top.material_name}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">{walls.length} wall{walls.length !== 1 ? "s" : ""}{shown !== walls.length ? ` · ${shown} shown` : ""}</div>
        </div>
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <MiniCount n={counts.attention} tone="text-purple-700" title="Changes requested" />
          <MiniCount n={counts.awaiting} tone="text-amber-700" title="Awaiting client" />
          <MiniCount n={counts.approved} tone="text-emerald-700" title="Approved" />
          <MiniCount n={counts.none} tone="text-zinc-400" title="No review" />
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-zinc-400 flex-shrink-0" strokeWidth={1.5} />
              : <ChevronRight className="w-4 h-4 text-zinc-300 flex-shrink-0" strokeWidth={1.5} />}
      </button>
      {open && (
        <div className="border-t border-zinc-100 bg-zinc-50/40">
          {groups.map((g) => (
            <div key={g.name || "ungrouped"}>
              {g.name && (
                <div className="flex items-center gap-1.5 px-4 pt-2.5 pb-1 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                  <Layers className="w-3 h-3" strokeWidth={1.5} /> {g.name}
                </div>
              )}
              <div className="divide-y divide-zinc-100">
                {g.walls.map((w) => <ReviewRow key={w.id} line={w} compact onOpen={() => onOpenWall(w)} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewRow({ line, onOpen, compact }) {
  const s = STATUS[line.client_design_approval || "na"] || STATUS.na;
  const locked = ["completed", "done"].includes(line.client_design_approval);
  return (
    <button onClick={onOpen} data-testid={`review-row-${line.id}`}
            className={`w-full flex items-center gap-3 ${compact ? "pl-6 pr-4 py-2.5" : "px-4 py-3"} text-left hover:bg-white transition-colors`}>
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-zinc-900 truncate">{compact ? wallLabel(line) : line.material_name}</div>
        {!compact && line.wall_no && <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">{line.wall_no}</div>}
      </div>
      <span className={`text-xs font-medium whitespace-nowrap ${s.text}`} data-testid={`review-status-${line.id}`}>
        {locked && <Lock className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={1.5} />}{s.label}
      </span>
      {line.current_design_version && (
        <span className="text-[10px] font-mono text-zinc-400 tabular-nums">V{line.current_design_version}</span>
      )}
      <ChevronRight className="w-4 h-4 text-zinc-300 flex-shrink-0" strokeWidth={1.5} />
    </button>
  );
}

function DesignDrawer({ projectCode, line, me, onClose, onSaved, canRelay }) {
  const [data, setData] = useState({ versions: [], comments: [] });
  const [loading, setLoading] = useState(true);
  const isLocked = ["completed", "done"].includes(line.client_design_approval);
  const isFeedback = ["modify", "rejected"].includes((line.client_design_approval || "").toLowerCase());

  useEffect(() => {
    api.get(`/projects/${projectCode}/boq/${line.id}/design-files`)
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [projectCode, line.id]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-zinc-200 rounded-2xl shadow-xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold tracking-tight truncate">{line.material_name}</h3>
            {line.wall_no && <div className="text-xs text-zinc-400 font-mono">{line.wall_no}</div>}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-lg"><X className="w-5 h-5" strokeWidth={1.5} /></button>
        </div>

        <div className="p-6 space-y-6">
          {isLocked && (
            <div className="flex items-center gap-2.5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" data-testid="design-locked-banner">
              <Lock className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
              <span>Approved by the client · locked. No further uploads.</span>
            </div>
          )}

          {/* CSM relays feedback → designer (append-only, locked) */}
          {isFeedback && canRelay && (
            <CSMRelaySection projectCode={projectCode} line={line} onSaved={onSaved} />
          )}

          {/* Versions */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2">
              {data.versions.length === 0 ? "Versions" : `${data.versions.length} version${data.versions.length === 1 ? "" : "s"}`}
            </div>
            {loading ? <div className="text-zinc-400 text-sm">Loading…</div>
              : data.versions.length === 0 ? (
                <div className="text-sm text-zinc-400 italic">No designs shared yet.</div>
              ) : (
                <div className="space-y-2">
                  {data.versions.map((v) => <VersionCard key={v.id} v={v} locked={isLocked} />)}
                </div>
              )}
          </div>

          {/* Design uploads happen ONLY in the designer's My Designs portal —
              this page is read-only versions + CSM feedback relay. */}
        </div>
      </div>
    </div>
  );
}

function CSMRelaySection({ projectCode, line, onSaved }) {
  const history = line.designer_instructions || [];
  const pending = !!line.pending_csm_relay;
  const clientComment = line.client_last_feedback_comment || "";
  const [adding, setAdding] = useState(pending && history.length === 0);
  const [text, setText] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);

  async function send() {
    if (!text.trim()) { toast.error("Write the instruction for the designer"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${projectCode}/boq/${line.id}/relay-design-feedback`,
                     { instruction: text.trim(), file_url: fileUrl, file_name: fileName });
      toast.success("Instruction sent to the designer.");
      setText(""); setFileUrl(""); setFileName(""); setAdding(false);
      onSaved && onSaved();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  }

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid="csm-relay-section">
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-700 mb-2">
        <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} /> Instructions to designer
      </div>
      {clientComment && (
        <div className="text-sm bg-white border border-zinc-200 rounded-lg p-3 mb-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">Client feedback (internal)</div>
          “{clientComment}”
        </div>
      )}

      {/* Locked instruction thread */}
      {history.length > 0 && (
        <div className="space-y-2 mb-3">
          {history.map((h, i) => (
            <div key={h.id || i} className="bg-white border border-zinc-200 rounded-lg p-3" data-testid={`relay-instruction-${i}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">#{i + 1} · {h.by} · {fmtIST(h.at)}</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-600"><Lock className="w-3 h-3" strokeWidth={1.5} /> locked</span>
              </div>
              <p className="text-sm text-zinc-800 whitespace-pre-line">{h.text}</p>
              {h.file_url && (
                <a href={h.file_url} target="_blank" rel="noreferrer"
                   className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-700 hover:underline">
                  <Paperclip className="w-3.5 h-3.5" strokeWidth={1.5} /> {h.file_name || "reference file"}
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add a new instruction */}
      {adding ? (
        <div className="space-y-2 bg-white border border-amber-200 rounded-lg p-3">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
                    placeholder="e.g. Enlarge the logo ~20% and use a warmer amber palette. Keep the layout."
                    className="w-full text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
                    data-testid="csm-relay-input" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1 flex items-center gap-1">
              <Paperclip className="w-3 h-3" strokeWidth={1.5} /> Clarity reference (optional)
            </div>
            <FileUpload value={fileUrl}
                        onChange={(u) => { setFileUrl(u); setFileName(u ? (u.split("/").pop() || "reference") : ""); }}
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.svg,.ai,.cdr,.eps,.zip" testid="csm-relay-file" />
          </div>
          <div className="flex justify-end gap-2">
            {history.length > 0 && <Btn variant="secondary" onClick={() => { setAdding(false); setText(""); setFileUrl(""); }}>Cancel</Btn>}
            <Btn onClick={send} disabled={saving || !text.trim()} className="bg-amber-500 hover:bg-amber-600" data-testid="csm-relay-send">
              {saving ? "Sending…" : "Send to designer"}
            </Btn>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} data-testid="csm-relay-add"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 hover:underline">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Add new instruction
        </button>
      )}
    </div>
  );
}

function VersionCard({ v, locked }) {
  const s = v.status === "approved" ? "text-emerald-700"
    : v.status === "modification_requested" ? "text-purple-700"
    : v.status === "internal_draft" ? "text-zinc-500" : "text-amber-700";
  const isImg = /\.(jpe?g|png|webp|gif|svg)(\?|$)/i.test(v.file_url || "");
  return (
    <div className="flex items-center gap-3 p-2.5 border border-zinc-100 rounded-lg">
      <div className="w-12 h-12 rounded-lg bg-zinc-50 border border-zinc-200 flex items-center justify-center overflow-hidden flex-shrink-0">
        {isImg ? <img src={v.file_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
          : <FileText className="w-5 h-5 text-zinc-400" strokeWidth={1.5} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] font-bold bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded">V{v.version_number}</span>
          <span className="text-sm font-medium truncate">{v.file_name}</span>
        </div>
        <div className="text-[10px] text-zinc-400 font-mono">{fmtIST(v.uploaded_at)} · {v.uploaded_by}</div>
        {v.designer_note && <div className="text-xs text-zinc-500 mt-0.5 italic truncate">“{v.designer_note}”</div>}
      </div>
      <span className={`text-[10px] font-mono uppercase tracking-wider ${s}`}>{(v.status || "pending").replace(/_/g, " ")}</span>
      {locked && (
        <Lock className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" strokeWidth={1.5} data-testid={`version-locked-${v.id}`} />
      )}
    </div>
  );
}
